import React from "react";
import {
  buildFrozenColumnCellStyle,
  buildFrozenColumnLayout,
  buildPinnedSectionDescriptors,
  normalizeFrozenColumnKeys,
  splitColumnsByFrozenState,
} from "../utils/dataManagerFrozenPanes.js";
import { DATA_MANAGER_GRID_SELECTION_COLUMN_KEY } from "../utils/dataManagerGridTableLayout.js";

const EMPTY_COLUMNS = [];

export function buildDataManagerFrozenGridColumns(columns, frozenColumnKeys, selectionColumnWidth) {
  const keys = normalizeFrozenColumnKeys(frozenColumnKeys, columns);
  const { pinnedColumns, scrollableColumns } = splitColumnsByFrozenState(columns, keys);
  const layout = buildFrozenColumnLayout(buildPinnedSectionDescriptors({
    freezableColumnDescriptors: [
      { key: DATA_MANAGER_GRID_SELECTION_COLUMN_KEY, width: selectionColumnWidth },
      ...columns,
    ],
    frozenColumnKeys: keys,
    pinSelectionColumn: keys.length > 0,
  }));
  return {
    columns: [...pinnedColumns, ...scrollableColumns].map((column) => ({
      ...column,
      frozenLayoutEntry: layout[column.key],
    })),
    selectionStyle: buildFrozenColumnCellStyle(layout[DATA_MANAGER_GRID_SELECTION_COLUMN_KEY]),
  };
}

// Server-confirmed preferences own persistence and rollback; no browser storage.
export default function useDataManagerGridFrozenPanes(columnPreferences, {
  leadingColumns = EMPTY_COLUMNS,
  trailingColumns = EMPTY_COLUMNS,
  selectionColumnWidth,
  active = true,
} = {}) {
  const { resolvedColumns, savedFrozenColumnKeys, handleFrozenColumnKeysChange } = columnPreferences;
  const [frozenKeys, setFrozenKeys] = React.useState(savedFrozenColumnKeys);
  const frozenKeysRef = React.useRef(frozenKeys);
  const [menu, setMenu] = React.useState(null);
  const columns = React.useMemo(
    () => [...leadingColumns, ...resolvedColumns, ...trailingColumns],
    [leadingColumns, resolvedColumns, trailingColumns],
  );

  React.useEffect(() => {
    frozenKeysRef.current = savedFrozenColumnKeys;
    setFrozenKeys(savedFrozenColumnKeys);
  }, [savedFrozenColumnKeys]);
  React.useEffect(() => {
    if (!active) setMenu(null);
  }, [active]);

  const changeFrozenKeys = (nextKeys) => {
    frozenKeysRef.current = nextKeys;
    setFrozenKeys(nextKeys);
    handleFrozenColumnKeysChange(nextKeys);
  };
  const grid = React.useMemo(
    () => buildDataManagerFrozenGridColumns(columns, frozenKeys, selectionColumnWidth),
    [columns, frozenKeys, selectionColumnWidth],
  );
  return {
    ...grid,
    selectionHeaderStyle: grid.selectionStyle ? { ...grid.selectionStyle, zIndex: 20 } : undefined,
    onColumnContextMenu: setMenu,
    menuProps: {
      canFreezePanes: true,
      columnKey: menu?.columnKey,
      columnLabel: menu?.columnLabel,
      hasFrozenPanes: frozenKeys.length > 0,
      isColumnFrozen: frozenKeys.includes(menu?.columnKey),
      isOpen: active && Boolean(menu),
      onClose: () => setMenu(null),
      onToggleColumnFreeze: (key) => changeFrozenKeys(
        frozenKeysRef.current.includes(key)
          ? frozenKeysRef.current.filter((entry) => entry !== key)
          : [...frozenKeysRef.current, key],
      ),
      onUnfreezePanes: () => changeFrozenKeys([]),
      position: menu,
    },
  };
}
